import React from 'react'
import { graphql } from 'relay-runtime'
import styled from '@emotion/styled'
import { useFragment } from 'react-relay'
import { MetaAdPreview$key } from './__generated__/MetaAdPreview.graphql'
import { facebookCallToActionButtonGrey } from '../../colors'
import facebookAdGlobe from '../../assets/facebookAdGlobe.png'
import { Button } from '@mui/material'
import { formatEnumString } from '../../common'
import { theme } from '../../mui'
import WarningIcon from '@mui/icons-material/Warning'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {
  MetaAdCallToAction,
} from '@prisma/client'

interface MetaAdPreviewProps {
  fragmentRequest: MetaAdPreview$key | null
}

export const metaAdPreviewFragment = graphql`
  fragment MetaAdPreview on MetaAd {
    id
    callToAction
    description
    destinationUrl
    displayUrl
    headline
    primaryText
    facebookPageId
    instagramAccountId
    facebookPage {
      name
      picture
    }
    instagramAccount {
      name
      picture
    }
    metaAdMetaAdSets {
      id
    }
    visualAssets {
      type
      url
    }
  }
`

export const Meta = {
  AdImage: styled.img`
    max-width: 100%;
  `,
  AdVideo: styled.video`
    max-width: 100%;
  `,
}

export const Facebook = {
  Wrapper: styled.div`
    padding-top: 0.5rem;
    background-color: ${theme.palette.common.white};
    color: ${theme.palette.common.black};
    max-height: 100%;
    overflow: auto;
  `,
  Header: styled.div`
    margin: 0 0.5rem;
    display: flex;
    align-items: center;
  `,
  Image: styled.img`
    border-radius: 50%;
    height: 2.5rem;
  `,
  HeaderText: styled.div`
    flex-grow: 1;
    font-weight: bold;
    font-size: 0.75rem;
    margin-left: 0.5rem;
  `,
  HeaderTextSubheader: styled.div`
    color: ${theme.palette.text.disabled};
    display: flex;
    font-weight: unset;
    font-size: 0.625rem;
    align-items: center;
  `,
  HeaderGlobe: styled.img`
    width: 0.5rem;
    height: 0.5rem;
  `,
  PrimaryText: styled.div`
    margin: 0.5rem;
    font-size: 0.75rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
  `,
  Footer: styled.div`
    font-size: 0.75rem;
    display: flex;
    margin: 1rem 0;
  `,
  FooterText: styled.div`
    flex-grow: 1;
    margin-left: 0.5rem;
  `,
  CallToAction: styled(Button)`
    margin: 0.5rem;
    padding: 0.0625rem 0.5rem;
    color: ${facebookCallToActionButtonGrey};
    font-weight: unset;
    font-size: 0.75rem;
    border: 1px solid ${facebookCallToActionButtonGrey};
    &:hover {
      border: 1px solid ${facebookCallToActionButtonGrey};
    }
  `,
  Url: styled.div`
    font-size: 0.625rem;
  `,
  Headline: styled.div`
    font-weight: bold;
  `,
  WarningIcon: styled(WarningIcon)`
    margin-right: 0.5rem;
  `,
  WarningWrapper: styled.div`
    padding: 1rem;
    align-items: center;
    display: flex;
  `,
}

const Instagram = {
  Wrapper: styled.div`
    background-color: ${theme.palette.common.white};
    color: ${theme.palette.common.black};
    max-height: 100%;
    overflow: auto;
  `,
  Header: styled.div`
    padding-left: 10px;
    padding-right: 10px;
    display: flex;
    align-items: center;
    height: 40px;
  `,
  Image: styled.img`
    border-radius: 33px;
    height: 26px;
    width: 26px;
  `,
  HeaderText: styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 0 8px;
    flex: 2 1 auto;
    order: 0;
  `,
  AccountName: styled.div`
    font-weight: bold;
    font-size: 0.75rem;
  `,
  HeaderTextSubheader: styled.div`
    display: flex;
    font-weight: 300;
    font-size: 0.625rem;
    align-items: center;
  `,
  HeaderGlobe: styled.img`
    width: 0.5rem;
    height: 0.5rem;
  `,
  PrimaryTextWrapper: styled.div`
    padding: 0 10px;
    padding-bottom: 10px;
    font-size: 0.75rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
  `,
  PrimaryText: styled.span`
    font-size: 0.75rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
  `,
  FooterWrapper: styled.div`
    padding-top: 0;
    padding-bottom: 0;
    padding-left: 10px;
    padding-right: 10px;
    font-size: 0.75rem;
    display: flex;
  `,
  Footer: styled.div`
    height: 40px;
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
    flex-grow: 1;
    display: flex;
    border-bottom: 1px solid lightgrey;
  `,
  FooterText: styled.div`
    align-items: flex-start;
    justify-content: flex-start;
    flex-direction: row;
    display: flex;
  `,
  CallToAction: styled(Button)`
    margin: 0.5rem;
    padding: 0.0625rem 0.5rem;
    color: ${facebookCallToActionButtonGrey};
    font-weight: unset;
    font-size: 0.75rem;
    border: 1px solid ${facebookCallToActionButtonGrey};
    &:hover {
      border: 1px solid ${facebookCallToActionButtonGrey};
    }
  `,
  CallToActionIconWrapper: styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: flex-end;
  `,
  BottomIconsWrapper: styled.div`
    height: 40px;
    padding-bottom: 4px;
    padding-top: 8px;
    padding-left: 10px;
    padding-right: 10px;
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
    display: flex;
  `,
  BottomIconsOnLeftWrapper: styled.div`
    flex: 0 1 auto;
    order: 0;
  `,
  BottomIconsOnRightWrapper: styled.div`
    flex: 0 1 auto;
    order: 0;
  `,
  BottomIconsOnLeft: styled.div`
    align-items: flex-start;
    justify-content: flex-start;
    flex-direction: row;
    display: flex;
  `,
  BottomIconsOnRight: styled.div`
    align-items: flex-start;
    justify-content: flex-start;
    flex-direction: row;
    display: flex;
  `,
  BottomIconOnLeft: styled.img`
    margin-left: 1px;
    margin-right: 15px;
  `,
  Url: styled.div`
    font-size: 0.625rem;
  `,
  Headline: styled.div`
    font-weight: bold;
  `,
  WarningIcon: styled(WarningIcon)`
    margin-right: 0.5rem;
  `,
  WarningWrapper: styled.div`
    padding: 1rem;
    align-items: center;
    display: flex;
  `,
}

const MetaAdPreview = ({ fragmentRequest }: MetaAdPreviewProps) => {
  const metaAd = useFragment(metaAdPreviewFragment, fragmentRequest)!
  let visualAsset = <></>
  if (
    metaAd == null ||
    metaAd.primaryText == null
  ) {
    return (
      <Facebook.WarningWrapper>
        <Facebook.WarningIcon color="warning" />
        Edit Text to Preview
      </Facebook.WarningWrapper>
    )
  }
  if (metaAd.facebookPageId == null && metaAd.instagramAccountId == null) {
    return (
      <Facebook.WarningWrapper>
        <Facebook.WarningIcon color="warning" />
        Select Facebook Page or Instagram Account to Preview
      </Facebook.WarningWrapper>
    )
  }
  if (metaAd.metaAdMetaAdSets.length == 0) {
    return (
      <Facebook.WarningWrapper>
        <Facebook.WarningIcon color="warning" />
        Select at Least One Ad Set to Preview
      </Facebook.WarningWrapper>
    )
  }
  if (metaAd.visualAssets.length === 0) {
    return (
      <Facebook.WarningWrapper>
        <Facebook.WarningIcon color="warning" />
        Upload Asset to Preview
      </Facebook.WarningWrapper>
    )
  }
  if (
    metaAd.visualAssets.length > 0 &&
    metaAd?.visualAssets[0].url.indexOf('mp4') > 0
  ) {
    visualAsset = (
      <Meta.AdVideo controls>
        <source src={metaAd.visualAssets[0].url} />
      </Meta.AdVideo>
    )
  } else if (metaAd.visualAssets.length > 0) {
    visualAsset = <Meta.AdImage src={metaAd.visualAssets[0].url} />
  }

  if (!!metaAd.facebookPageId) {
    return (
      <>
        <Facebook.Wrapper>
          <Facebook.Header>
            <Facebook.Image src={metaAd.facebookPage?.picture} />
            <Facebook.HeaderText>
              {metaAd.facebookPage?.name}
              <Facebook.HeaderTextSubheader>
                Sponsored ·&nbsp;
                <Facebook.HeaderGlobe src={facebookAdGlobe} />
              </Facebook.HeaderTextSubheader>
            </Facebook.HeaderText>
            <MoreHorizIcon />
          </Facebook.Header>
          <Facebook.PrimaryText>{metaAd.primaryText}</Facebook.PrimaryText>
          <div>{visualAsset}</div>
          <Facebook.Footer>
            <Facebook.FooterText>
              <Facebook.Url>{metaAd.displayUrl}</Facebook.Url>
              <Facebook.Headline>{metaAd.headline}</Facebook.Headline>
              {metaAd.description}
            </Facebook.FooterText>
            <Facebook.CallToAction variant="outlined">
              {formatEnumString(metaAd.callToAction || MetaAdCallToAction.LEARN_MORE)}
            </Facebook.CallToAction>
          </Facebook.Footer>
        </Facebook.Wrapper>
      </>
    )
  } else {
    return (
      <>
        <Instagram.Wrapper>
          <Instagram.Header>
            <Instagram.Image src={metaAd.instagramAccount?.picture} />
            <Instagram.HeaderText>
              <Instagram.AccountName>
                {metaAd.instagramAccount?.name}
              </Instagram.AccountName>
              <Instagram.HeaderTextSubheader>
                Sponsored
              </Instagram.HeaderTextSubheader>
            </Instagram.HeaderText>
            <MoreHorizIcon />
          </Instagram.Header>
          <div>{visualAsset}</div>
          <Instagram.FooterWrapper>
            <Instagram.Footer>
              <Instagram.FooterText>
                {formatEnumString(metaAd.callToAction || MetaAdCallToAction.LEARN_MORE)}
              </Instagram.FooterText>
              <Instagram.CallToActionIconWrapper>
                <ChevronRightIcon />
              </Instagram.CallToActionIconWrapper>
            </Instagram.Footer>
          </Instagram.FooterWrapper>
          <Instagram.BottomIconsWrapper>
            <Instagram.BottomIconsOnLeftWrapper>
              <Instagram.BottomIconsOnLeft>
                <Instagram.BottomIconOnLeft src="https://facebook.com/images/instagram/ads/preview/Like.png" />
                <Instagram.BottomIconOnLeft src="https://facebook.com/images/instagram/ads/preview/Comment.png" />
                <Instagram.BottomIconOnLeft src="https://facebook.com/images/instagram/ads/preview/direct-share.png" />
              </Instagram.BottomIconsOnLeft>
            </Instagram.BottomIconsOnLeftWrapper>
            <Instagram.BottomIconsOnRightWrapper>
              <Instagram.BottomIconsOnRight>
                <img src="https://www.facebook.com/images/instagram/ads/preview/save.png" />
              </Instagram.BottomIconsOnRight>
            </Instagram.BottomIconsOnRightWrapper>
          </Instagram.BottomIconsWrapper>
          <Instagram.PrimaryTextWrapper>
            <b>{metaAd.instagramAccount?.name}</b> {metaAd.primaryText}
          </Instagram.PrimaryTextWrapper>
        </Instagram.Wrapper>
      </>
    )
  }
}

export default MetaAdPreview
